import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Shield, Target, Heart, Globe2, Globe, Hand, Handshake, ToolCase, Factory, Landmark } from 'lucide-react';
import TiltCard from './TiltCard';
import ContactBanner from './ContactBanner';


export default function AboutPage({onPageChange} : {onPageChange: (page: string) => void}) {
    const focusAreas = [
      {
        title: "Trade Advocacy",
        description: "Strong representation of member interests in international trade and policy forums.",
        icon: <Globe className="w-6 h-6" />,
        color: "bg-yellow-400",
        image: "https://images.unsplash.com/photo-1646041385844-52e977c8531b?q=80&w=1228&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D", 
      },
      {
        title: "Sustainability",
        description: "Promoting environmentally and socially sustainable sugar production practices.",
        icon: <Target className="w-6 h-6" />,
        color: "bg-green-400",
        image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?q=80&w=1313&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      },
      {
        title: "Community",
        description: "Supporting the livelihoods and development of sugar-producing communities.",
        icon: <Heart className="w-6 h-6" />,
        color: "bg-rose-400",
        image: "https://images.unsplash.com/photo-1675608683088-858ff9913272?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      },
      {
        title: "Cooperation",
        description: "Building partnerships across countries and organizations for mutual benefit.",
        icon: <Handshake className="w-6 h-6" />,
        color: "bg-blue-400",
        image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1074&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      },
    ];

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">About Us</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            The ACP/LDC Sugar Industries Group has been the voice of sugar industries across African, 
            Caribbean, Pacific and Least Developed Countries for over seven decades.
          </p>
        </div>

        {/* Mission and Vision */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          <div>
            <ImageWithFallback
              src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
              alt="Sugar production facility"
              className="rounded-lg shadow-lg w-full h-80 object-cover"
            />
          </div>
          <div className="flex flex-col justify-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-lg text-gray-600 mb-6">
              To represent, advocate for, and support the interests of sugar industries across ACP and LDC countries 
              in matters of trade policy, development cooperation, and agricultural sustainability.
            </p>
            <p className="text-lg text-gray-600">
              We work to ensure that sugar industries in developing countries can compete fairly in global markets 
              while contributing to sustainable development and poverty reduction in their communities.
            </p>
          </div>
        </div>

        {/* Our Values */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
             {focusAreas.map((area, index) => (
              <div
                key={index}
                className="relative bg-white rounded-3xl shadow-lg group overflow-hidden flex flex-col justify-between transition-all duration-300 hover:shadow-2xl hover:scale-105"
              >
                 {/* Icon Overlap */}
                <div className="absolute top-30 right-5  z-20">
                  <div className={`${area.color} text-white p-4 rounded-xl shadow-md group-hover:scale-105 transition-transform duration-300`}>
                    {area.icon}
                  </div>
                </div>

                {/* Image Section */}
                <div className="w-full h-40 mt-auto">
                  <ImageWithFallback
                    src={area.image}
                    alt={area.title}
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    className="object-cover"
                  />
                </div>

                {/* Text Content */}
                <div className="p-6 pb-10 z-10">
                  <h3 className="text-lg font-semibold text-forest-green mb-2">{area.title}</h3>
                  <p className="text-slate-700 text-sm leading-relaxed mb-4 line-clamp-3">
                    {area.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* History Overview */}
        <section className="mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">A Rich History</h2>
              <p className="text-lg text-gray-600 mb-6">
                Our organization traces its roots back to the 1950s with the Commonwealth Sugar Agreement, 
                and possibly even earlier. We have evolved through various forms to become the unified voice 
                of ACP and LDC sugar industries we are today.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Throughout the decades, we have navigated changing global trade landscapes, from colonial 
                preferences to modern multilateral trade agreements, always working to secure the best 
                outcomes for our member industries.
              </p>
              <p className="text-lg text-gray-600">
                Today, we continue this legacy of advocacy and support, adapting to new challenges while 
                maintaining our commitment to the sustainable development of sugar industries across our member countries.
              </p>
            </div>
            <div className='h-full'>
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1714402585193-5463ff86d69d?q=80&w=1332&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="Historical sugar plantation"
                className="rounded-lg shadow-lg w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Key Activities */}
        <section className="bg-gray-50 rounded-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-50">What We Do</h2>
          <div className="flex relative">
            <TiltCard className='w-[50%]'>
              <div className='bg-red-50 text-card-foreground flex flex-col gap-6 rounded-xl px-8 py-6 group-hover:shadow-lg'>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Trade Advocacy</h3>
                <ul className="space-y-2 text-gray-600">
                    <li>• Representation in EU and UK trade negotiations</li>
                    <li>• Policy analysis and position development</li>
                    <li>• Market access facilitation</li>
                    <li>• Technical standards coordination</li>
                </ul>
              </div>
              <Globe2 className="scale-600 text-red-600 opacity-50 absolute right-30 top-1/2 translate-y-[-50%]"/>
            </TiltCard>
            <TiltCard className='w-[50%] top-[-50%] absolute right-10 z-5'>
              <div className='bg-green-50 text-card-foreground flex flex-col gap-6 rounded-xl px-8 py-6 shadow-lg'>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Development Support</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Capacity building programs</li>
                  <li>• Technology transfer initiatives</li>
                  <li>• Sustainable agriculture promotion</li>
                  <li>• Climate adaptation strategies</li>
                </ul>
                <ToolCase className="scale-600 text-green-600 opacity-50 absolute right-30 top-1/2 translate-y-[-50%]"/>
              </div>
            </TiltCard>
          </div>
          <div className="flex relative mt-20">
            <TiltCard className='w-[50%]'>
              <div className='bg-blue-50 text-card-foreground flex flex-col gap-6 rounded-xl px-8 py-6 group-hover:shadow-lg'>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Industry Coordination</h3>
                <ul className="space-y-2 text-gray-600">
                    <li>• Information sharing networks</li>
                    <li>• Best practice dissemination</li>
                    <li>• Joint research initiatives</li>
                    <li>• Quality standards development</li>
                </ul>
              </div>
              <Factory className="scale-600 text-blue-600 opacity-50 absolute right-30 top-1/2 translate-y-[-50%]"/>
            </TiltCard>
            <TiltCard className='w-[50%] top-[-50%] absolute right-10 z-5'>
              <div className='bg-amber-50 text-card-foreground flex flex-col gap-6 rounded-xl px-8 py-6 shadow-lg'>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Stakeholder Engagement</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Government liaison activities</li>
                  <li>• International organization partnerships</li>
                  <li>• Private sector collaboration</li>
                  <li>• Civil society dialogue</li>
                </ul>
                <Landmark className="scale-600 text-amber-600 opacity-50 absolute right-30 top-1/2 translate-y-[-50%]"/>
              </div>
            </TiltCard>
          </div>
        </section>

        {/* Contact Information */}
        <section className="w-full flex items-center">
          <ContactBanner onPageChange={onPageChange} />
        </section>
        {/* <section className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Get in Touch</h2>
          <p className="text-lg text-gray-600 mb-8">
            We welcome collaboration and engagement from all stakeholders interested in the sustainable 
            development of sugar industries in ACP and LDC countries.
          </p>
          <div className="bg-amber-50 rounded-lg p-8 inline-block">
            <p className="text-gray-700">
              <strong>Email:</strong> info@acpsugar.org<br />
              <strong>Address:</strong> To be confirmed
            </p>
          </div>
        </section> */}
      </div>
    </div>
  );
}